import { PAGE_SEO } from '../seo/pageSeoConfig';

function toolSeo(path, content) {
  const seo = PAGE_SEO[path];
  return {
    path,
    title: seo.title,
    description: seo.description,
    h1: seo.h1,
    ...content,
  };
}

export const PUBLIC_TOOL_SEO = {
  roomDecoration: toolSeo('/ai-tools/ai-room-decoration', {
    headingSubtext: 'Stop Guessing. See Your Home Decor Ideas in Your Actual Room.',
    initialDecoration: 'Full Decoration Redesign',
    toolName: 'AI Room Decoration Tool',
    featuresTitle: 'AI Room Decoration Features',
    features: [
      'Decorate real room photos with new furniture, colors, lighting, and interior style ideas.',
      'Generate complete room decoration concepts for bedrooms, living rooms, kitchens, offices, and more.',
      'Choose room type, design style, color preferences, image quality, and optional furniture details.',
      'Preserve the original room proportions while exploring a fresh decorated look.',
    ],
    howItWorksTitle: 'How the AI Room Decoration Tool Works',
    howItWorksSteps: [
      'Upload a clear photo of the room you want to decorate.',
      'Choose the room type, interior design style, colors, and optional furniture notes.',
      'The AI creates a decorated room concept based on your original photo and preferences.',
      'Review the design preview, adjust your choices, and generate another decoration direction if needed.',
    ],
    relatedTools: [
      { label: 'AI 3D Model to Realistic Interior Image', path: '/ai-tools/ai-3d-model-to-realistic-interior' },
      { label: 'AI Room Furniture Replacement', path: '/ai-tools/ai-furniture-replacement' },
      { label: 'AI Exterior Design', path: '/ai-tools/ai-exterior-design' },
      { label: 'AI Floor Plan to Realistic Room Generator', path: '/ai-tools/ai-2d-floorplan-to-room-render' },
    ],
  }),
  interior3dModelToRealistic: toolSeo('/ai-tools/ai-3d-model-to-realistic-interior', {
    headingSubtext:
      'Turn Your 3D Interior Model Screenshot Into a Photorealistic Room Image.',
    initialDecoration: 'Full Decoration Redesign',
    designTool: 'interior-3d-model',
    toolType: 'interior_3d_model_to_realistic',
    shortLabel: '3D Model to Realistic Interior',
    toolName: 'AI 3D Model to Realistic Interior Image',
    uploadHeading: 'Upload a 3D model screenshot',
    uploadDropzone: 'Upload a 3D model screenshot',
    uploadHelper:
      'Upload a screenshot from Blender, 3ds Max, Maya, SketchUp, Three.js, or any 3D interior model viewer. The AI will convert it into a realistic interior image while preserving the room structure, dimensions, camera angle, and openings.',
    generateCta: 'Generate Realistic Interior',
    noImageError: 'Please choose a 3D model screenshot first.',
    featuresTitle: 'AI 3D Model to Realistic Interior Features',
    features: [
      'Convert 3D interior model viewport screenshots into photorealistic interior photographs.',
      'Preserve modeled room structure, dimensions, camera angle, doors, windows, and openings.',
      'Choose room type, interior style, decoration level, colors, and optional furniture notes.',
      'Improve materials, lighting, furniture, and staging while keeping the same architectural space.',
    ],
    howItWorksTitle: 'How the AI 3D Model to Realistic Interior Tool Works',
    howItWorksSteps: [
      'Upload a screenshot from Blender, 3ds Max, Maya, SketchUp, Three.js, or any 3D model viewer.',
      'Choose the room type, interior design style, decoration level, colors, and optional notes.',
      'The AI converts the 3D viewport capture into a realistic interior photograph of the same space.',
      'Review the result, adjust your settings, and generate another realistic interior direction if needed.',
    ],
    relatedTools: [
      { label: 'AI Room Decoration', path: '/ai-tools/ai-room-decoration' },
      { label: 'AI 2D Floor Plan to 3D Transformer', path: '/ai-tools/ai-2d-floorplan-to-3d' },
      { label: 'AI Floor Plan to Realistic Room Generator', path: '/ai-tools/ai-2d-floorplan-to-room-render' },
    ],
  }),
  furnitureReplacement: toolSeo('/ai-tools/ai-furniture-replacement', {
    headingSubtext: 'Preview New Furniture Ideas in Your Actual Room Before You Move Anything.',
    initialDecoration: 'Light Refresh',
    toolName: 'AI Furniture Replacement Tool',
    featuresTitle: 'AI Furniture Replacement Features',
    features: [
      'Replace furniture in bedrooms, living rooms, kitchens, offices, and more from one uploaded photo.',
      'Keep the original room structure recognizable while exploring new furniture layouts and styles.',
      'Choose room type, furniture direction, interior style, colors, image quality, and custom notes.',
      'Preview furniture replacement ideas before buying, moving, or staging real pieces.',
    ],
    howItWorksTitle: 'How the AI Furniture Replacement Tool Works',
    howItWorksSteps: [
      'Upload a room photo that clearly shows the current furniture and room structure.',
      'Choose the room type, interior style, colors, and optional furniture replacement notes.',
      'The AI keeps the room recognizable and generates a new furniture-focused preview.',
      'Review the replacement concept and adjust your notes or style for another direction.',
    ],
    relatedTools: [
      { label: 'AI Room Decoration', path: '/ai-tools/ai-room-decoration' },
      { label: 'AI Garden Design', path: '/ai-tools/ai-garden-design' },
      { label: 'AI 2D Floor Plan to 3D Transformer', path: '/ai-tools/ai-2d-floorplan-to-3d' },
    ],
  }),
  exteriorDesign: toolSeo('/ai-tools/ai-exterior-design', {
    headingSubtext: 'See Exterior Renovation Ideas on Your Real Home Before You Start.',
    toolName: 'AI Exterior Home Design Tool',
    featuresTitle: 'AI Exterior Design Features',
    features: [
      'Transform building exterior photos into realistic facade redesign previews.',
      'Explore exterior styles, facade materials, weather, time of day, and custom color directions.',
      'Preserve the building structure, camera view, windows, doors, and main proportions.',
      'Create visual ideas for home renovation, curb appeal, and modern exterior upgrades.',
    ],
    howItWorksTitle: 'How the AI Exterior Home Design Tool Works',
    howItWorksSteps: [
      'Upload a clear photo of your home or building exterior.',
      'Choose an exterior style, change level, facade material, environment, and colors.',
      'The AI keeps the main structure and generates a realistic exterior redesign preview.',
      'Review the facade concept and adjust the style or materials for another direction.',
    ],
    relatedTools: [
      { label: 'AI Garden Design', path: '/ai-tools/ai-garden-design' },
      { label: 'AI Room Decoration', path: '/ai-tools/ai-room-decoration' },
      { label: 'AI Room Furniture Replacement', path: '/ai-tools/ai-furniture-replacement' },
    ],
  }),
  gardenDesign: toolSeo('/ai-tools/ai-garden-design', {
    headingSubtext: 'Visualize Landscaping Ideas in Your Real Outdoor Space Before You Plant.',
    toolName: 'AI Garden Design Tool',
    featuresTitle: 'AI Garden Design Features',
    features: [
      'Generate garden and outdoor landscape concepts from a real yard, patio, or backyard photo.',
      'Explore planting styles, garden change levels, outdoor layouts, and landscaping directions.',
      'Use visible AI previews to plan outdoor upgrades before changing hardscape or planting.',
      'Create ideas for backyards, front gardens, patios, terraces, and exterior living spaces.',
    ],
    howItWorksTitle: 'How the AI Garden Design Tool Works',
    howItWorksSteps: [
      'Upload a photo of the garden, patio, yard, or outdoor area you want to redesign.',
      'Choose a garden style and change level, then add notes about plants, paths, seating, or features.',
      'The AI uses the existing outdoor photo to create a landscaping preview.',
      'Review the garden idea and refine your notes or style choices for a new concept.',
    ],
    relatedTools: [
      { label: 'AI Exterior Design', path: '/ai-tools/ai-exterior-design' },
      { label: 'AI Room Decoration', path: '/ai-tools/ai-room-decoration' },
      { label: 'AI 2D Floor Plan to 3D Transformer', path: '/ai-tools/ai-2d-floorplan-to-3d' },
    ],
  }),
  floorPlanTo3d: toolSeo('/ai-tools/ai-2d-floorplan-to-3d', {
    headingSubtext: 'Turn Your 2D Floor Plan Into a Clear 3D Home Preview.',
    toolName: 'AI 2D Floor Plan to 3D Transformer',
    featuresTitle: 'AI 2D Floor Plan to 3D Transformer Features',
    features: [
      'Upload a 2D floor plan image and convert it into an open-top 3D-style home preview.',
      'Choose Render Style, Furnishing, 3D View Angle, Interior Style, and image quality before generating.',
      'Add optional notes for colors, flooring, furniture, or layout direction in your 3D preview.',
      'Create spatial visualizations for planning, presentations, listings, and design exploration.',
    ],
    howItWorksTitle: 'How the AI 2D Floor Plan to 3D Transformer Works',
    howItWorksSteps: [
      'Upload a clear 2D floor plan image in a supported image format.',
      'Set Render Style, Furnishing, 3D View Angle, Interior Style, additional notes, and image quality.',
      'The tool prepares your floor plan and sends your settings to the AI generator.',
      'Review the 3D preview and adjust options to generate another version.',
    ],
    relatedTools: [
      { label: 'AI Floor Plan to Realistic Room Generator', path: '/ai-tools/ai-2d-floorplan-to-room-render' },
      { label: 'AI 2D Floor Plan Designer', path: '/ai-tools/ai-2d-floorplan-designer' },
      { label: 'AI Room Decoration', path: '/ai-tools/ai-room-decoration' },
    ],
  }),
  floorPlanRoomRender: toolSeo('/ai-tools/ai-2d-floorplan-to-room-render', {
    headingSubtext: 'See How a Floor Plan Room Could Look as a Real Interior.',
    toolName: 'AI Floor Plan to Realistic Room Generator',
    featuresTitle: 'AI Floor Plan to Realistic Room Generator Features',
    features: [
      'Select a room area from a 2D floor plan and turn it into a realistic interior preview.',
      'Choose the room type, design style, and notes for the AI room render.',
      'Frame the target space so the AI focuses on the room or area you want to visualize.',
      'Create realistic interior concepts from architectural plans before a room is built or staged.',
    ],
    howItWorksTitle: 'How the AI Floor Plan to Realistic Room Generator Works',
    howItWorksSteps: [
      'Upload a floor plan and frame the room or area you want to render.',
      'Prepare the selected area so the room boundaries are clear for the AI.',
      'Choose the room type, interior design style, and optional notes.',
      'Generate a realistic room preview based on the selected floor plan area.',
    ],
    relatedTools: [
      { label: 'AI 2D Floor Plan to 3D Transformer', path: '/ai-tools/ai-2d-floorplan-to-3d' },
      { label: 'AI 2D Floor Plan Designer', path: '/ai-tools/ai-2d-floorplan-designer' },
      { label: 'AI Room Decoration', path: '/ai-tools/ai-room-decoration' },
    ],
  }),
  floorPlanCreator: toolSeo('/ai-tools/ai-2d-floorplan-creator', {
    headingSubtext: 'Describe Your Home Layout and Preview a Floor Plan Concept.',
    toolName: 'AI Floor Plan Creator',
    featuresTitle: 'AI Floor Plan Creator Features',
    features: [
      'Describe room counts, home size, layout needs, and style notes for a generated 2D floor plan.',
      'Create early layout concepts before drawing or modeling a floor plan manually.',
      'Use AI-generated floor plan ideas as a starting point for renovation, new-build, or planning workflows.',
      'Explore architecture and room layout options alongside other MyRoomsAI floor plan tools.',
    ],
    howItWorksTitle: 'How the AI Floor Plan Creator Works',
    howItWorksSteps: [
      'Enter the home details, room requirements, layout goals, and any special planning notes.',
      'The AI interprets the brief and prepares a 2D floor plan concept.',
      'Review the generated layout direction and refine the details for another plan.',
      'Use the result as a planning reference or continue with other floor plan visualization tools.',
    ],
    relatedTools: [
      { label: 'AI 2D Floor Plan Designer', path: '/ai-tools/ai-2d-floorplan-designer' },
      { label: 'AI 2D Floor Plan to 3D Transformer', path: '/ai-tools/ai-2d-floorplan-to-3d' },
      { label: 'AI Floor Plan to Realistic Room Generator', path: '/ai-tools/ai-2d-floorplan-to-room-render' },
    ],
  }),
  floorPlanDesigner: toolSeo('/ai-tools/ai-2d-floorplan-designer', {
    headingSubtext: 'Draw Walls, Rooms, Doors, and Windows in a Browser-Based 2D Floor Plan Editor.',
    toolName: 'AI 2D Floor Plan Designer',
    featuresTitle: 'AI 2D Floor Plan Designer Features',
    features: [
      'Draw walls manually with snapping, node editing, and wall splitting on the shared floor plan editor.',
      'Add doors and windows, detect closed rooms, and select room areas for downstream AI workflows.',
      'Preview your layout in 3D directly from the editor without uploading an image first.',
      'Export floor plan JSON for use with MyRoomsAI 3D and room render tools.',
    ],
    howItWorksTitle: 'How the AI 2D Floor Plan Designer Works',
    howItWorksSteps: [
      'Open the designer and start with an empty canvas ready for manual drawing.',
      'Draw walls, move nodes, and place doors and windows using the sidebar tools.',
      'Select detected rooms and preview the plan in 3D to validate layout and flow.',
      'Export your plan or continue with other MyRoomsAI floor plan visualization tools.',
    ],
    relatedTools: [
      { label: 'AI 2D Floor Plan to 3D Transformer', path: '/ai-tools/ai-2d-floorplan-to-3d' },
      { label: 'AI Floor Plan to Realistic Room Generator', path: '/ai-tools/ai-2d-floorplan-to-room-render' },
      { label: 'AI Floor Plan Creator', path: '/ai-tools/ai-2d-floorplan-creator' },
    ],
  }),
};
